import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { Grid, FormControlLabel, Checkbox, FormControl, InputLabel, Select, MenuItem, Button, Box, CircularProgress } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { getDateFilter } from './HedisMetricService';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { TimePeriodFilterEnum } from '../../Enum/TimePeriodFilterEnum';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers';
import { useAppDispatch } from '../../store/store';
import b64toBlob from 'b64-to-blob';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import { HedisMetricsTypeEnum } from '../../Enum/HedisMetricsTypeEnum';
import { fetchTeams } from '../../store/careTeams.slice';
import TeamDropdown from '../Shared/TeamDropdown';
import HealthPlanMultiSelect from '../Shared/HealthPlanMultiSelect';
import { exportHedisDetail, exportBhDiagnosticSummary } from '../../components/HedisMetrics/HedisMetricService';
import { fetchPopulations } from '../Population/PopulationService';
import Population from '../../Models/Population/Population.model';

const FilterOptions = (props) => {
    const dispatch = useAppDispatch();
    const {
        selectedTeam,
        setSelectedTeam,
        dateFilter,
        setDateFilter,
        fromDate,
        setFromDate,
        toDate,
        setToDate,
        healthPlan,
        setHealthPlan,
        includePastOpenStatusMembers,
        setIncludePastOpenStatusMembers,
        isDetailPage,
        hedisType,
        showInActiveTeams,
        setShowInActiveTeams,
        hasElevatedFilterAccess,
        pop,
        setPop
    } = props;
    const { careTeams: teams } = useSelector((state: RootState) => state.careTeams);
    const dateFilterList = getDateFilter().filter((d) => d.Value !== 'Custom' && d.Value !== 'Current Month');
    const [downLoading, setDownLoading] = useState<boolean>(false);
    const [populations, setPopulations] = useState<Population[]>([]);

    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: 50 * 4.5,
                width: 250,
            },
        },
    };

    const healthPlanChange = useCallback(
        async (
            selectedHealthPlans: any,
            canResetPopulation = true
        ) => {
            setHealthPlan(selectedHealthPlans);
            setPopulations([]);
            if (canResetPopulation) {
                setPop?.(0);
            }

            const selectedHealthPlanIds =
                getSelectedHealthPlanIds(selectedHealthPlans);

            try {
                const isAllHealthPlansSelected =
                    selectedHealthPlans === 'All' ||
                    selectedHealthPlans?.includes?.('All') ||
                    selectedHealthPlanIds.length === 0;

                const healthPlanIdsToFetch = isAllHealthPlansSelected
                    ? healthPlan
                        .map((healthPlan) => healthPlan.Id)
                        .filter(Boolean)
                    : selectedHealthPlanIds;

                if (healthPlanIdsToFetch.length === 0) {
                    setPopulations([]);
                    return;
                }

                const populationResponses = await Promise.all(
                    healthPlanIdsToFetch.map((healthPlanId) =>
                        fetchPopulations(healthPlanId, true)
                    )
                );

                const combinedPopulations =
                    populationResponses.flat();

                const uniquePopulations = Array.from(
                    new Map(
                        combinedPopulations.map((item) => [
                            item.Id,
                            item,
                        ])
                    ).values()
                );

                setPopulations(uniquePopulations);
            } catch (error) {
                console.error(
                    'Error fetching populations:',
                    error
                );

                setPopulations([]);
                setPop?.(0);
            }
        },
        [
            healthPlan,
            setHealthPlan,
            setPop,
        ]
    );

    const initialPage = useCallback(async () => {
        await healthPlanChange(healthPlan, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [healthPlan]);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    useEffect(() => {
        dispatch(fetchTeams(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const changeDateFilter = (filter: any) => {
        setFromDate(null);
        setToDate(null);
        setDateFilter(parseInt(filter));
    };

    const changeFromDate = (date: any) => {
        setFromDate(date);
        let dateInfo = new Date(date);
        if (!Boolean(toDate) || new Date(toDate) <= dateInfo) {
            let endDate = new Date(dateInfo.setDate(dateInfo.getDate() + 30));
            setToDate(endDate);
        }
        setDateFilter(TimePeriodFilterEnum.Custom);
    };

    const changeToDate = (date: any) => {
        setToDate(date);
        let dateInfo = new Date(date);
        if (!Boolean(fromDate) || new Date(fromDate) >= dateInfo) {
            let startDate = new Date(dateInfo.setDate(dateInfo.getDate() - 30));
            setFromDate(startDate);
        }
        setDateFilter(TimePeriodFilterEnum.Custom);
    };

    const handleOnIncludePastStatus = (isChecked: boolean) => {
        setIncludePastOpenStatusMembers(isChecked);
    };

    const exportDetail = async () => {
        setDownLoading(true);
        let start = fromDate !== null ? new Date(fromDate).toDateString() : null;
        let end = toDate !== null ? new Date(toDate).toDateString() : null;
        const data = hedisType === HedisMetricsTypeEnum.BHDiagnosticDescription || hedisType === HedisMetricsTypeEnum.BHDiagnostic ?
            await exportBhDiagnosticSummary(hedisType, healthPlan, selectedTeam, dateFilter, start, end, includePastOpenStatusMembers, showInActiveTeams) :
            await exportHedisDetail(hedisType, healthPlan, selectedTeam, dateFilter, start, end, includePastOpenStatusMembers, Number(pop) || 0, showInActiveTeams);
        let type = hedisType === 'ProviderVisits' ? HedisMetricsTypeEnum.ProviderVisitsDescription : typeof hedisType === 'number' ? HedisMetricsTypeEnum[hedisType] : hedisType;
        let blob = b64toBlob(data.base64String, 'text/csv');
        let url = window.URL.createObjectURL(blob);
        let link = document.createElement('a');
        link.href = url;
        var date = new Date();
        var mm = date.getMonth() + 1; // getMonth() is zero-based
        var dd = date.getDate();
        var dateInfo = [(mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd, date.getFullYear()].join('');
        link.setAttribute('download', `${type} - ${dateInfo}.xlsx`);
        link.click();
        link.remove();

        setDownLoading(false);
    };

    const getSelectedHealthPlanIds = (value: any): string[] => {
        if (!value) {
            return [];
        }

        if (Array.isArray(value)) {
            return value
                .map((item) => {
                    if (typeof item === 'object') {
                        return item?.Id ?? item?.id ?? item?.Value ?? item?.value;
                    }

                    return item;
                })
                .filter((id) => id !== null && id !== undefined && id !== '' && id !== 'All')
                .map(String);
        }

        if (value === 'All') {
            return [];
        }

        return String(value)
            .split(',')
            .map((id) => id.trim())
            .filter((id) => id && id !== 'All');
    };

    const handleDownload = async () => {
        try {
            setDownLoading(true);
            await exportDetail(); // your existing function
        } finally {
            setDownLoading(false);
        }
    };

    const populationOptions = useMemo(
        () => populations.map((item) => String(item.PopulationNumber)),
        [populations]
    );

    useEffect(() => {
        if (pop !== 0 && populationOptions.length && !populationOptions.includes(String(pop))) {
            setPop?.(0);
        }
    }, [pop, populationOptions]);

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            {hasElevatedFilterAccess && (
                <Grid size={isDetailPage ? 2 : 3}>
                    <TeamDropdown
                        teams={teams}
                        selectedTeam={selectedTeam}
                        onChange={(id) => setSelectedTeam(id)}
                        showInactive={showInActiveTeams}
                        onToggleInactive={setShowInActiveTeams}
                    />
                </Grid>
            )}
            <Grid size={isDetailPage ? 2 : 3}>
                <HealthPlanMultiSelect
                    value={healthPlan}
                    onChange={setHealthPlan}
                    menuProps={MenuProps}
                />
            </Grid>
            {(hedisType === HedisMetricsTypeEnum.BP || hedisType === HedisMetricsTypeEnum.BpDescription || hedisType === HedisMetricsTypeEnum.HgbA1c || hedisType === HedisMetricsTypeEnum.HgbA1cDescription) && (
                <Grid size={isDetailPage ? 2 : 3}>
                    <FormControl size="small" fullWidth>
                        <InputLabel id="population-label">
                            Population
                        </InputLabel>
                        <Select
                            labelId="population-label"
                            id="population-select"
                            value={pop}
                            label="Population"
                            name="Population"
                            onChange={(event) => {
                                setPop?.(Number(event.target.value) || 0);
                            }}
                            MenuProps={MenuProps}
                        >
                            <MenuItem value={0}>
                                All
                            </MenuItem>
                            {populations.map((populationItem) => {
                                const populationId = populationItem.PopulationNumber;
                                return (
                                    <MenuItem key={populationId} value={populationId}>
                                        {populationItem.PopulationName}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                </Grid>
            )}
            <Grid size={isDetailPage ? 2 : 3}>
                <FormControl size="small" fullWidth>
                    <InputLabel>Date Filter</InputLabel>
                    <Select value={dateFilter} label="Date Filter" name="Date" onChange={(e) => changeDateFilter(e.target.value)}>
                        {dateFilterList.map((date) => (
                            <MenuItem key={date.Id} value={date.Id}>
                                {date.Value}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            {
                parseInt(dateFilter) === TimePeriodFilterEnum.Custom ? (
                    <React.Fragment>
                        <Grid size={isDetailPage ? 1.5 : 3}>
                            <DesktopDatePicker
                                label="From Date"
                                value={Boolean(fromDate) ? fromDate : null}
                                minDate={new Date('2017-01-01')}
                                onChange={(newValue) => changeFromDate(newValue)}
                                slotProps={{ textField: { fullWidth: true, size: "small" } }}
                            />
                        </Grid>
                        <Grid size={isDetailPage ? 1.5 : 3}>
                            <DesktopDatePicker
                                label="To Date"
                                value={Boolean(toDate) ? toDate : null}
                                minDate={new Date('2017-01-01')}
                                onChange={(newValue) => changeToDate(newValue)}
                                slotProps={{ textField: { fullWidth: true, size: "small" } }}
                            />
                        </Grid>
                        {isDetailPage ? (
                            <Grid size={1}>
                                <Button style={{ minWidth: '120px', marginLeft: 'auto' }} onClick={() => exportDetail()} variant="contained" color="success">
                                    {downLoading ? 'Exporting..' : 'Export'}
                                </Button>
                            </Grid>
                        ) : null}
                    </React.Fragment>
                ) : isDetailPage ? (
                    <React.Fragment>
                        {(hedisType === HedisMetricsTypeEnum.BP || hedisType === HedisMetricsTypeEnum.BpDescription || hedisType === HedisMetricsTypeEnum.HgbA1c || hedisType === HedisMetricsTypeEnum.HgbA1cDescription) ? (
                            <Grid size={2.8}>
                            </Grid>) : <Grid size={4.8}>
                        </Grid>}
                        <Grid size={1}>
                            <Button style={{ minWidth: '120px', marginLeft: 'auto' }} onClick={() => exportDetail()} variant="contained" color="success">
                                {downLoading ? 'Exporting...' : 'Export'}
                            </Button>
                        </Grid>
                        <Grid size={hasElevatedFilterAccess ? 4.9 : 6.9}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={includePastOpenStatusMembers}
                                        size="small"
                                        name="Include Past Open Status Members"
                                        onChange={(e) => handleOnIncludePastStatus(e.target.checked)}
                                    />
                                }
                                label="Include members who have ever been in any open status"
                            />
                        </Grid>
                    </React.Fragment>
                ) : (
                    <Grid size={12}>
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            width="100%"
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={includePastOpenStatusMembers}
                                        size="small"
                                        name="Include Past Open Status Members"
                                        onChange={(e) =>
                                            handleOnIncludePastStatus(e.target.checked)
                                        }
                                    />
                                }
                                label="Include members who have ever been in any open status"
                            />

                            <Box
                                onClick={!downLoading ? handleDownload : undefined}
                                sx={{
                                    width: 24,
                                    height: 24,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    cursor: downLoading ? 'default' : 'pointer',
                                }}
                            >
                                {downLoading ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    <FileDownloadIcon
                                        sx={{
                                            color: 'black',
                                            fontSize: 24,
                                        }}
                                        titleAccess="Export"
                                    />
                                )}
                            </Box>
                        </Box>
                    </Grid>
                )
            }
        </LocalizationProvider>
    );
};

export default FilterOptions;